/**
 * MODULE: _shared/frontend-shell/src/components/assistant/assistantContextAdvisorChipAction.ts
 * PURPOSE: Build structured context actions from advisor chip labels [2.2.1250].
 * PUBLIC_API: advisorChipContextAction
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-05-26 [2.2.1254] — entity-linked draft chips use compose_draft (CRM email compose).
 */

import type { AssistantContextAction } from '@shared/lib/frontend/api/assistantContextApi';

const COMPOSE_DRAFT_LABEL_RE = /\b(draft|check-in email|check in email)\b/i;
const ADVISOR_EXECUTE_LABEL_RE = /\b(summarize|log a call|schedule executive|follow up)\b/i;

export type AdvisorChipEntity = {
  entity_type?: string;
  entity_id?: string;
};

/** Wrap a chip label so AssistantPage routes navigate, compose, or auto-send. */
export function advisorChipContextAction(
  label: string,
  entity?: AdvisorChipEntity,
): AssistantContextAction {
  const text = label.trim();
  const entityType = (entity?.entity_type || '').trim();
  const entityId = (entity?.entity_id || '').trim();
  const hasEntity = Boolean(entityType && entityId);

  if (hasEntity && COMPOSE_DRAFT_LABEL_RE.test(text)) {
    return {
      label: text,
      action: 'draft_email',
      execution_mode: 'compose_draft',
      entity_type: entityType,
      entity_id: entityId,
    };
  }

  if (ADVISOR_EXECUTE_LABEL_RE.test(text)) {
    return {
      label: text,
      action: 'summarize_activity',
      execution_mode: 'advisor_execute',
      ...(hasEntity ? { entity_type: entityType, entity_id: entityId } : {}),
    };
  }

  return {
    label: text,
    action: 'advisor_execute',
    execution_mode: 'advisor_execute',
    ...(hasEntity ? { entity_type: entityType, entity_id: entityId } : {}),
  };
}
